/**
 * Document Management Page (書類管理)
 *
 * Admin page for viewing and managing document submissions. Data from empaccount
 * (status: submitted, resubmitted, resubmission_requested, cleared). Indicators: orange = 提出済み,
 * blue = 再提出済み, yellow = 再提出依頼中, green = 完了.
 */
import React, { useState, useEffect, useCallback } from 'react';
import SubmissionTable from './SubmissionTable';
import CheckSubmissions from './checkSubmissions/CheckSubmissions';
import FinalizationModal from './checkSubmissions/FinalizationModal';
import { Submission } from './types';
import { API_BASE_URL } from '../../../config';
import './DocumentManagement.css';
import filterIcon from '../../../assests/icons/filter_icon.png';
import completedIcon from '../../../assests/icons/completed.png';

export interface DocumentManagementProps {
  /** Submission data from API/DB; when provided, list is controlled */
  submissions?: Submission[];
  /** Called when user clicks 開く or 続きから再開 in the table */
  onOperationClick?: (submission: Submission) => void;
  /** After prepare-rehire from archive manage modal */
  onStartRehire?: (participant: { email: string; name: string }) => void;
}

const DocumentManagement: React.FC<DocumentManagementProps> = ({
  submissions = [],
  onOperationClick,
  onStartRehire,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  /** Status filter: '' = すべて, or 提出済み / 再提出済み / 再提出依頼中 / 完了 */
  const [statusFilter, setStatusFilter] = useState<Submission['status'] | ''>('');
  /** Rows from empaccount (submitted, resubmitted, cleared) — from API */
  const [submissionList, setSubmissionList] = useState<Submission[]>([]);
  /** When set, show Check Submissions UI (opened by 開く from table) */
  const [checkSubmissionsSubmission, setCheckSubmissionsSubmission] = useState<Submission | null>(null);
  /** Manual finalize (あとで skipped or from 書類管理) */
  const [finalizeTarget, setFinalizeTarget] = useState<Submission | null>(null);

  /** Refreshes rows and operation column (続きから再開 vs 開く) from server after review changes. */
  const loadSubmissionList = useCallback(() => {
    fetch(`${API_BASE_URL}/api/employees/document-management-list`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load list'))))
      .then((rows: {
        id: string;
        status: string;
        empName: string;
        email: string;
        requestDate: string;
        submittedDate: string;
        submittedTime: string;
        hasUnfinishedChecking?: boolean;
        invitationId?: string;
        finalizedAt?: string;
        needsFinalize?: boolean;
      }[]) => {
        const list: Submission[] = (Array.isArray(rows) ? rows : []).map((row) => {
          const status: Submission['status'] =
            row.status === 'cleared'
              ? 'completed'
              : row.status === 'resubmission_requested'
                ? 'resubmission_requested'
                : row.status === 'resubmitted'
                  ? 'resubmitted'
                  : 'submitted';
          let operationType: Submission['operationType'] = row.hasUnfinishedChecking ? 'resume' : 'open';
          if (status === 'completed' && row.needsFinalize) {
            operationType = 'finalize';
          } else if (status === 'completed' && row.finalizedAt) {
            operationType = 'view';
          }
          return {
            id: row.id,
            status,
            empName: row.empName || '',
            email: row.email || '',
            requestDate: row.requestDate || '',
            submittedDate: row.submittedDate || '',
            submittedTime: row.submittedTime || '',
            operationType,
            invitationId: row.invitationId || undefined,
            finalizedAt: row.finalizedAt || undefined,
            needsFinalize: row.needsFinalize === true,
          };
        });
        setSubmissionList(list);
      })
      .catch(() => setSubmissionList([]));
  }, []);

  useEffect(() => {
    loadSubmissionList();
  }, [loadSubmissionList]);

  const isControlled = submissions.length > 0;
  const displaySubmissions = isControlled ? submissions : submissionList;

  /** Apply search (by name) and status filter (提出済み / 再提出済み / 完了) */
  const filteredSubmissions = displaySubmissions.filter((s) => {
    const matchesSearch = s.empName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || statusFilter === s.status;
    return matchesSearch && matchesStatus;
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleStatusFilterChange = (value: Submission['status'] | '') => {
    setStatusFilter(value);
  };

  const handleOperationClick = (submission: Submission) => {
    if (submission.operationType === 'finalize') {
      setFinalizeTarget(submission);
      return;
    }
    if (submission.operationType === 'view') {
      setCheckSubmissionsSubmission(submission);
      return;
    }
    onOperationClick?.(submission);
    setCheckSubmissionsSubmission(submission);
  };

  const handleCloseCheckSubmissions = () => {
    setCheckSubmissionsSubmission(null);
    loadSubmissionList();
  };

  const handleFinalizeComplete = () => {
    setFinalizeTarget(null);
    loadSubmissionList();
  };

  if (finalizeTarget?.email?.trim() && finalizeTarget.invitationId?.trim()) {
    return (
      <div className="document-management">
        <FinalizationModal
          employeeName={finalizeTarget.empName?.trim() || ''}
          employeeEmail={finalizeTarget.email.trim()}
          invitationId={finalizeTarget.invitationId.trim()}
          onClose={handleFinalizeComplete}
          onComplete={handleFinalizeComplete}
        />
      </div>
    );
  }

  if (checkSubmissionsSubmission !== null) {
    const isArchiveView =
      checkSubmissionsSubmission.status === 'completed' &&
      checkSubmissionsSubmission.operationType === 'view';
    return (
      <div className="document-management__check-submissions-wrap">
        <CheckSubmissions
          mode={isArchiveView ? 'archive' : 'review'}
          submission={checkSubmissionsSubmission}
          archiveInvitationId={checkSubmissionsSubmission.invitationId}
          archiveFinalizedAt={checkSubmissionsSubmission.finalizedAt}
          onClose={handleCloseCheckSubmissions}
          onStartRehire={onStartRehire}
        />
      </div>
    );
  }

  return (
    <div className="document-management">
      <h1 className="document-management__title">提出確認</h1>

      <div className="document-management__controls">
        <form className="document-management__search" onSubmit={handleSearch}>
          <input
            type="text"
            className="document-management__search-input"
            placeholder=""
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="document-management__search-btn">
            検索
          </button>
        </form>
        <div className="document-management__filter-wrapper">
          <button
            type="button"
            className="document-management__filter-btn"
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Filter options"
          >
            <img src={filterIcon} alt="" className="document-management__filter-icon" aria-hidden />
          </button>
          {showFilters && (
            <div className="document-management__filter-dropdown">
              <div className="document-management__filter-group">
                <label>ステータス</label>
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    handleStatusFilterChange(
                      e.target.value === '' ? '' : (e.target.value as Submission['status'])
                    )
                  }
                  aria-label="提出済み・再提出済み・再提出依頼中・完了で絞り込み"
                >
                  <option value="">すべて</option>
                  <option value="submitted">提出済み</option>
                  <option value="resubmitted">再提出済み</option>
                  <option value="resubmission_requested">再提出依頼中</option>
                  <option value="completed">完了</option>
                </select>
              </div>
            </div>
          )}
        </div>
        <div className="document-management__legend">
          <span className="document-management__legend-item">
            <span className="document-management__legend-dot document-management__legend-dot--orange" aria-hidden />
            提出済み
          </span>
          <span className="document-management__legend-item">
            <span className="document-management__legend-dot document-management__legend-dot--blue" aria-hidden />
            再提出済み
          </span>
          <span className="document-management__legend-item">
            <span className="document-management__legend-dot document-management__legend-dot--yellow" aria-hidden />
            再提出依頼中
          </span>
          <span className="document-management__legend-item">
            <img src={completedIcon} alt="" className="document-management__legend-icon" aria-hidden />
            完了
          </span>
        </div>
      </div>

      <div className="document-management__table-area">
        <SubmissionTable
          submissions={filteredSubmissions}
          onOperationClick={handleOperationClick}
        />
      </div>
    </div>
  );
};

export default DocumentManagement;
